// Failing-first stub (task 4.1): directional-heading selection and the single
// y-sorted painter's-algorithm draw list (design D10, UI.md §8). Pure, Pixi-free —
// bees, flowers, hive cells, and the queen all sort into one list by screen depth,
// never per-layer.

/** 12 headings × 30°: the sprite-sheet frame index for a velocity vector, no rotation
 * (heading changes swap frames — UI.md §8). */
export function headingIndex(vx: number, vy: number): number {
  // Compass-style angle (0° = "up", clockwise), matching the 12 × 30° directional
  // sprite sheets (UI.md §8) — the same convention the `prototype/` mock uses.
  const deg = (Math.atan2(vx, -vy) * 180) / Math.PI;
  const normalized = ((deg % 360) + 360) % 360;
  return Math.round(normalized / 30) % 12;
}

/** Anything that can appear in the single y-sorted draw list, keyed by its already-
 * projected screen y (painter's algorithm: lower screen y drawn first). A drawable whose
 * rendered sprite rises above its ground anchor (the own-hive tile) MAY carry an explicit
 * `depth` — its painter's-algorithm sort key at the sprite's top edge — so ground-level
 * neighbours clustered at its base (with a larger `screenY`) sort after it and paint over it
 * (hive-depth-order). Absent, `screenY` is the key. */
export interface Drawable {
  screenY: number;
  /** Optional explicit painter's-algorithm depth key (screen px). When set it overrides
   * `screenY` in {@link paintDepth} — a tall tile keys at its top edge; ground-level
   * drawables leave it unset and key on their (live) `screenY`. */
  depth?: number;
}

/** The one shared painter's-algorithm depth key both the pure list sort and the renderer's
 * per-node `zIndex` apply (hive-depth-order): a drawable's explicit `depth` when set (a tall
 * tile keyed at its top edge), otherwise the supplied `screenY`. The list sort passes each
 * drawable's snapshot `screenY`; the renderer passes the node's live/interpolated `screenY`,
 * so a keyless moving bee's paint key follows its rendered position rather than its stale
 * snapshot. Pure. */
export function paintDepth(drawable: Drawable, screenY: number): number {
  return drawable.depth ?? screenY;
}

/** Returns a new array of `drawables` ordered back-to-front by the shared depth key
 * ({@link paintDepth} at each drawable's snapshot `screenY`) — one list across every drawable
 * kind, not per-layer. The sort is stable, so equal-key drawables keep their insertion order. */
export function sortDrawables<T extends Drawable>(drawables: readonly T[]): T[] {
  return [...drawables].sort((a, b) => paintDepth(a, a.screenY) - paintDepth(b, b.screenY));
}
